import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "../components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { toast } from "sonner";
import { Plus, Search, Ban, Trash2, AlertTriangle, UserX } from "lucide-react";
import api from "../lib/api";
import { format } from "date-fns";
import { de } from "date-fns/locale";
import { useAuth } from "../context/AuthContext";

const BRAND_COLOR = "#c11718";

export default function BlacklistPage() {
  const { user } = useAuth();
  const [blacklist, setBlacklist] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [formData, setFormData] = useState({
    first_name: "",
    last_name: "",
    reason: "",
    notes: "",
    blocked_date: format(new Date(), "yyyy-MM-dd")
  });

  useEffect(() => {
    fetchBlacklist();
  }, []);

  const fetchBlacklist = async () => {
    try {
      const response = await api.get("/blacklist");
      setBlacklist(response.data);
    } catch (error) {
      toast.error("Fehler beim Laden der Blacklist");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await api.post("/blacklist", formData);
      toast.success("Zur Blacklist hinzugefügt");
      setDialogOpen(false);
      setFormData({
        first_name: "",
        last_name: "",
        reason: "",
        notes: "",
        blocked_date: format(new Date(), "yyyy-MM-dd")
      });
      fetchBlacklist();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Fehler beim Hinzufügen");
    }
  };

  const handleDelete = async (id) => {
    if (!confirm("Möchten Sie diesen Eintrag wirklich entfernen?")) return;
    
    try {
      await api.delete(`/blacklist/${id}`);
      toast.success("Von der Blacklist entfernt");
      fetchBlacklist();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Fehler beim Entfernen");
    }
  };

  const filteredList = blacklist.filter(item =>
    `${item.first_name} ${item.last_name}`.toLowerCase().includes(searchTerm.toLowerCase()) ||
    item.reason?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-slate-900"></div>
      </div>
    );
  }

  // Admin değilse sadece görüntüleme
  const isAdmin = user?.role === "admin";

  return (
    <div className="space-y-4 sm:space-y-6 p-4 sm:p-6" data-testid="blacklist-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold tracking-tight text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Blacklist
          </h1>
          <p className="text-sm text-slate-500 mt-1">Gesperrte ehemalige Bewohner</p>
        </div>
        {isAdmin && (
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button style={{ backgroundColor: BRAND_COLOR }} data-testid="add-blacklist-btn">
                <Plus className="h-4 w-4 mr-2" />
                Zur Blacklist hinzufügen
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-md">
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <Ban className="h-5 w-5 text-red-600" />
                  Zur Blacklist hinzufügen
                </DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-2">
                    <Label>Vorname *</Label>
                    <Input
                      value={formData.first_name}
                      onChange={(e) => setFormData(prev => ({ ...prev, first_name: e.target.value }))}
                      required
                      data-testid="blacklist-firstname"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Nachname *</Label>
                    <Input
                      value={formData.last_name}
                      onChange={(e) => setFormData(prev => ({ ...prev, last_name: e.target.value }))}
                      required
                      data-testid="blacklist-lastname"
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>Grund *</Label>
                  <Input
                    value={formData.reason}
                    onChange={(e) => setFormData(prev => ({ ...prev, reason: e.target.value }))}
                    placeholder="z.B. Mietschulden, Sachbeschädigung..."
                    required
                    data-testid="blacklist-reason"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Notizen</Label>
                  <textarea
                    value={formData.notes}
                    onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
                    className="w-full h-20 px-3 py-2 text-sm border border-slate-200 rounded-md"
                    placeholder="Zusätzliche Informationen..."
                    data-testid="blacklist-notes"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Sperrdatum</Label>
                  <Input
                    type="date"
                    value={formData.blocked_date}
                    onChange={(e) => setFormData(prev => ({ ...prev, blocked_date: e.target.value }))}
                    data-testid="blacklist-date"
                  />
                </div>
                <Button type="submit" className="w-full" style={{ backgroundColor: BRAND_COLOR }}>
                  Hinzufügen
                </Button>
              </form>
            </DialogContent>
          </Dialog>
        )}
      </div>

      {/* Search */}
      <div className="relative max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
        <Input
          placeholder="Name oder Grund suchen..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="pl-10"
          data-testid="blacklist-search"
        />
      </div>

      {/* Stats */}
      <Card className="border-red-200 bg-red-50/50">
        <CardContent className="p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-red-100 rounded-lg">
              <UserX className="h-5 w-5 text-red-600" />
            </div>
            <div>
              <p className="text-xs text-red-600">Gesperrte Personen</p>
              <p className="text-xl font-bold text-red-700">{blacklist.length}</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Table */}
      {filteredList.length > 0 ? (
        <Card className="border-slate-200">
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="bg-slate-50">
                    <TableHead>Name</TableHead>
                    <TableHead>Grund</TableHead>
                    <TableHead>Notizen</TableHead>
                    <TableHead>Sperrdatum</TableHead>
                    {isAdmin && <TableHead className="text-right">Aktionen</TableHead>}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredList.map((item) => (
                    <TableRow key={item.id}>
                      <TableCell className="font-medium">
                        <div className="flex items-center gap-2">
                          <Ban className="h-4 w-4 text-red-500" />
                          {item.first_name} {item.last_name}
                        </div>
                      </TableCell>
                      <TableCell>
                        <span className="px-2 py-1 bg-red-100 text-red-700 text-xs rounded">
                          {item.reason}
                        </span>
                      </TableCell>
                      <TableCell className="max-w-[200px] truncate text-slate-500">
                        {item.notes || "-"}
                      </TableCell>
                      <TableCell>
                        {item.blocked_date ? format(new Date(item.blocked_date), "dd.MM.yyyy", { locale: de }) : "-"}
                      </TableCell>
                      {isAdmin && (
                        <TableCell className="text-right">
                          <Button
                            variant="ghost"
                            size="sm"
                            className="text-red-600 hover:text-red-700 hover:bg-red-50"
                            onClick={() => handleDelete(item.id)}
                            data-testid={`delete-blacklist-${item.id}`}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </TableCell>
                      )}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>
      ) : (
        <Card className="border-slate-200">
          <CardContent className="py-12 text-center">
            <AlertTriangle className="h-12 w-12 mx-auto text-slate-300 mb-3" />
            <p className="text-slate-500">
              {searchTerm ? "Keine Einträge gefunden" : "Keine Einträge in der Blacklist"}
            </p>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
